import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Button,
	Container,
	PasswordInput,
	Paper,
	Stack,
	Text,
	Title
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconMailCheck } from "@tabler/icons-react";
import { useHistory, useLocation } from "react-router-dom";
import * as API from "../../helpers/api";
import config from "../../config";

export default function ResetPasswordPage() {
	const location = useLocation();
	const history = useHistory();
	const token = useMemo(
		() => new URLSearchParams(location.search).get("token") || "",
		[location.search]
	);
	const [loading, setLoading] = useState(false);
	const [validating, setValidating] = useState(true);
	const [isValidToken, setIsValidToken] = useState(false);
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	useEffect(() => {
		let ignore = false;

		const validateToken = async () => {
			if (!token) {
				setValidating(false);
				setIsValidToken(false);
				return;
			}

			try {
				await API.post(config.api.resetPasswordValidate, { token });
				if (!ignore) {
					setIsValidToken(true);
				}
			} catch (_) {
				if (!ignore) {
					setIsValidToken(false);
				}
			} finally {
				if (!ignore) {
					setValidating(false);
				}
			}
		};

		validateToken();
		return () => {
			ignore = true;
		};
	}, [token]);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!newPassword || !confirmPassword) {
			notifications.show({
				color: "red",
				title: "Password reset failed",
				message: "Please complete both password fields."
			});
			return;
		}

		if (newPassword !== confirmPassword) {
			notifications.show({
				color: "red",
				title: "Passwords do not match",
				message: "Please confirm the new password exactly."
			});
			return;
		}

		setLoading(true);
		try {
			await API.post(config.api.resetPasswordComplete, {
				token,
				newPassword
			});
			notifications.show({
				color: "teal",
				title: "Password reset complete",
				message: "You can sign in with the new password now."
			});
			history.replace("/login");
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				error?.response?.data?.error ||
				error?.message ||
				"Unable to reset password.";
			notifications.show({
				color: "red",
				title: "Password reset failed",
				message
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container size={460} py="xl">
			<Paper withBorder radius="lg" shadow="xl" p="xl">
				<Stack spacing="lg">
					<Stack spacing={6}>
						<Title order={2}>Reset password</Title>
						<Text color="dimmed" size="sm">
							Choose a new password using the secure reset link that was sent to your email.
						</Text>
					</Stack>

					{validating ? (
						<Alert color="blue" icon={<IconMailCheck size={16} />}>
							Validating reset link…
						</Alert>
					) : null}

					{!validating && !isValidToken ? (
						<Alert color="red" icon={<IconAlertTriangle size={16} />}>
							This password reset link is invalid or expired. Ask an administrator to send a new one.
						</Alert>
					) : null}

					{!validating && isValidToken ? (
						<form onSubmit={handleSubmit}>
							<Stack spacing="md">
								<PasswordInput
									label="New password"
									placeholder="Enter a new password"
									value={newPassword}
									onChange={(event) => setNewPassword(event.currentTarget.value)}
									required
								/>
								<PasswordInput
									label="Confirm new password"
									placeholder="Repeat the new password"
									value={confirmPassword}
									onChange={(event) => setConfirmPassword(event.currentTarget.value)}
									required
								/>
								<Button type="submit" loading={loading} fullWidth>
									Set new password
								</Button>
							</Stack>
						</form>
					) : null}

					<Button variant="subtle" color="gray" onClick={() => history.replace("/login")}>
						Back to login
					</Button>
				</Stack>
			</Paper>
		</Container>
	);
}
